// src/features/simulators/online-checkin/components/OnlineCheckInConfigForm.tsx

import React from 'react';
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Grid,
} from '@mui/material';
import {
  Key,
  QrCode,
  CreditCard,
  Phone,
  Link as LinkIcon,
  VpnKey as KeyDeliveryIcon,
} from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import { MyFormSection } from '@/components/forms/MyFormLayout';
import { ReservationNumberControl } from '../../shared';
import { LinkAccessMethod } from '../types';
import { KeyDeliveryMethod } from '../../shared/types/journey.types';

export interface OnlineCheckInConfigValues {
  linkAccessMethod: LinkAccessMethod;
  keyDeliveryMethod: KeyDeliveryMethod;
  reservationNumber: string;
  autoGenerateReservation: boolean;
}

interface OnlineCheckInConfigFormProps {
  values: OnlineCheckInConfigValues;
  onChange: (values: OnlineCheckInConfigValues) => void;
}

export const OnlineCheckInConfigForm: React.FC<OnlineCheckInConfigFormProps> = ({
  values,
  onChange,
}) => {
  const translate = useTranslate();

  const update = (partial: Partial<OnlineCheckInConfigValues>) => {
    onChange({ ...values, ...partial });
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <MyFormSection
          title={translate('resources.simulators.online_checkin.link_access.label')}
          icon={<LinkIcon />}
          variant="outlined"
        >
          <FormControl component="fieldset">
            <RadioGroup
              value={values.linkAccessMethod}
              onChange={(e) => update({ linkAccessMethod: e.target.value as LinkAccessMethod })}
            >
              <FormControlLabel
                value={LinkAccessMethod.EMAIL}
                control={<Radio size="small" />}
                label={translate('resources.simulators.online_checkin.link_access.email')}
              />
              <FormControlLabel
                value={LinkAccessMethod.SMS}
                control={<Radio size="small" />}
                label={translate('resources.simulators.online_checkin.link_access.sms')}
              />
              <FormControlLabel
                value={LinkAccessMethod.DIRECT}
                control={<Radio size="small" />}
                label={translate('resources.simulators.online_checkin.link_access.direct')}
              />
            </RadioGroup>
          </FormControl>

          <ReservationNumberControl
            value={values.reservationNumber}
            autoGenerate={values.autoGenerateReservation}
            onValueChange={(value) => update({ reservationNumber: value })}
            onAutoGenerateChange={(autoGenerate) => update({ autoGenerateReservation: autoGenerate })}
            prefix="RES"
          />
        </MyFormSection>
      </Grid>

      <Grid item xs={12} md={6}>
        <MyFormSection
          title={translate('resources.simulators.online_checkin.key_delivery.label')}
          icon={<KeyDeliveryIcon />}
          variant="outlined"
        >
          <FormControl fullWidth>
            <InputLabel>{translate('resources.simulators.online_checkin.key_delivery.label')}</InputLabel>
            <Select
              value={values.keyDeliveryMethod}
              onChange={(e) => update({ keyDeliveryMethod: e.target.value as KeyDeliveryMethod })}
              label={translate('resources.simulators.online_checkin.key_delivery.label')}
            >
              <MenuItem value={KeyDeliveryMethod.MAGNETIC_CARD}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CreditCard fontSize="small" />
                  {translate('resources.simulators.kiosk.key_delivery.magnetic_card')}
                </Box>
              </MenuItem>
              <MenuItem value={KeyDeliveryMethod.RFID_CARD}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CreditCard fontSize="small" />
                  {translate('resources.simulators.kiosk.key_delivery.rfid_card')}
                </Box>
              </MenuItem>
              <MenuItem value={KeyDeliveryMethod.CONVENTIONAL_KEY}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Key fontSize="small" />
                  {translate('resources.simulators.kiosk.key_delivery.conventional_key')}
                </Box>
              </MenuItem>
              <MenuItem value={KeyDeliveryMethod.DIGITAL_PIN}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Phone fontSize="small" />
                  {translate('resources.simulators.kiosk.key_delivery.digital_pin')}
                </Box>
              </MenuItem>
              <MenuItem value={KeyDeliveryMethod.DIGITAL_MOBILE}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Phone fontSize="small" />
                  {translate('resources.simulators.kiosk.key_delivery.digital_mobile')}
                </Box>
              </MenuItem>
              <MenuItem value={KeyDeliveryMethod.QR_DISPENSER}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <QrCode fontSize="small" />
                  {translate('resources.simulators.kiosk.key_delivery.qr_dispenser')}
                </Box>
              </MenuItem>
              <MenuItem value={KeyDeliveryMethod.MANUAL}>
                {translate('resources.simulators.kiosk.key_delivery.manual')}
              </MenuItem>
            </Select>
          </FormControl>
        </MyFormSection>
      </Grid>
    </Grid>
  );
};
